import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../authorization/AuthorizationUtils";
import { api, baseUrl } from "../../services/Api";

interface MemberRow {
  id: string;
  displayName?: string | null;
  mail?: string | null;
  blocked?: boolean;
}

type CustomersTab = "customers" | "blocked";

function matchesNameOrEmail(m: MemberRow, q: string): boolean {
  const name = (m.displayName ?? "").toLowerCase();
  const mail = (m.mail ?? "").toLowerCase();
  return name.includes(q) || mail.includes(q);
}

const Customers = () => {
  const { instance } = useMsal();
  const myOid = instance.getActiveAccount()?.idTokenClaims?.oid as string | undefined;

  const [tab, setTab] = useState<CustomersTab>("customers");
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const filteredMembers = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => matchesNameOrEmail(m, q));
  }, [members, filterText]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const path =
        tab === "customers"
          ? "/admin/groups/customers/members"
          : "/admin/groups/blocked/members";
      const data = await api.get(path);
      setMembers((data?.members as MemberRow[]) ?? []);
    } catch (e: unknown) {
      let msg = "Failed to load members";
      if (e instanceof Response) {
        const res = e;
        msg = await res.text().catch(() => String(res.statusText));
      } else if (e instanceof Error) {
        msg = e.message;
      }
      setError(msg);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setFilterText("");
  }, [tab]);

  const onBlock = async (userId: string) => {
    if (
      !window.confirm(
        "Block this user? They will lose access to Dentifier until an administrator helps."
      )
    ) {
      return;
    }
    setBusyId(userId);
    setError(null);
    try {
      const token = await getToken();
      if (!token || !baseUrl) {
        throw new Error("Not signed in or API URL missing");
      }
      const res = await fetch(
        `${baseUrl.replace(/\/$/, "")}/admin/groups/customers/block/${encodeURIComponent(userId)}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: "{}",
        }
      );
      if (!res.ok) {
        const t = await res.text().catch(() => res.statusText);
        throw new Error(t || `HTTP ${res.status}`);
      }
      setMembers((prev) =>
        prev.map((m) => (m.id === userId ? { ...m, blocked: true } : m))
      );
    } catch (e: unknown) {
      let msg = "Block failed";
      if (e instanceof Error) {
        msg = e.message;
      }
      setError(msg);
    } finally {
      setBusyId(null);
    }
  };

  const onUnblock = async (userId: string) => {
    if (
      !window.confirm(
        "Unblock this user? They will be able to use Dentifier again after they sign in (or refresh their session)."
      )
    ) {
      return;
    }
    setBusyId(userId);
    setError(null);
    try {
      await api.delete(`/admin/groups/blocked/members/${encodeURIComponent(userId)}`);
      setMembers((prev) => prev.filter((m) => m.id !== userId));
    } catch (e: unknown) {
      let msg = "Unblock failed";
      if (e instanceof Response) {
        const res = e;
        msg = await res.text().catch(() => String(res.statusText));
      } else if (e instanceof Error) {
        msg = e.message;
      }
      setError(msg);
    } finally {
      setBusyId(null);
    }
  };

  const countLabel =
    tab === "customers"
      ? `${members.length} member${members.length === 1 ? "" : "s"} in this group`
      : `${members.length} blocked user${members.length === 1 ? "" : "s"}`;

  return (
    <div className="customers-page">
      <h1>Customers</h1>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v: CustomersTab) => setTab(v)}
          aria-label="customers and blocked tabs"
        >
          <Tab label="All customers" value="customers" />
          <Tab label="Blocked users" value="blocked" />
        </Tabs>
      </Box>

      {tab === "customers" ? (
        <Typography
          component="p"
          variant="body1"
          className="customers-intro"
          sx={{ mb: 4 }}
        >
          Everyone listed here can use Dentifier as a customer. Use Block to revoke access; blocked
          users are not removed from this list but can no longer use the application.
        </Typography>
      ) : (
        <Typography
          component="p"
          variant="body1"
          className="customers-intro"
          sx={{ mb: 4 }}
        >
          Users removed from the blocked group can use Dentifier again after they sign in or refresh
          their session.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
              {countLabel}
              {filterText.trim() !== "" &&
                ` · ${filteredMembers.length} match${filteredMembers.length === 1 ? "es" : ""} this filter`}
            </Typography>
            <TextField
              size="small"
              label="Search"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Name or email…"
              inputProps={{
                "aria-label":
                  tab === "customers"
                    ? "Filter customers by name or email"
                    : "Filter blocked users by name or email",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      {tab === "customers"
                        ? "No members in this group, or the Dentifier customers group is not configured in the API."
                        : "No blocked users, or the blocked group is not configured in the API."}
                    </TableCell>
                  </TableRow>
                ) : filteredMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>No members match this filter.</TableCell>
                  </TableRow>
                ) : tab === "customers" ? (
                  filteredMembers.map((m) => {
                    const self = myOid && m.id === myOid;
                    const isBusy = busyId === m.id;
                    return (
                      <TableRow key={m.id}>
                        <TableCell>{m.displayName ?? "—"}</TableCell>
                        <TableCell>{m.mail ?? "—"}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={!!self || isBusy || !!m.blocked}
                            onClick={() => onBlock(m.id)}
                            aria-label={
                              self
                                ? "You cannot block your own account"
                                : m.blocked
                                  ? "Blocked"
                                  : isBusy
                                    ? "Blocking…"
                                    : "Block user"
                            }
                          >
                            {isBusy ? "…" : m.blocked ? "Blocked" : "Block"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  filteredMembers.map((m) => {
                    const self = myOid && m.id === myOid;
                    const isBusy = busyId === m.id;
                    return (
                      <TableRow key={m.id}>
                        <TableCell>{m.displayName ?? "—"}</TableCell>
                        <TableCell>{m.mail ?? "—"}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            disabled={!!self || isBusy}
                            onClick={() => onUnblock(m.id)}
                            aria-label={
                              self
                                ? "You cannot unblock your own account this way"
                                : isBusy
                                  ? "Unblocking…"
                                  : "Unblock user"
                            }
                          >
                            {isBusy ? "…" : "Unblock"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </div>
  );
};

export default Customers;
